import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ArrowLeft, BarChart3, Brain, HeartPulse, AlertTriangle,
  ChevronDown, ChevronUp, BookOpen, Shield
} from "lucide-react";
import axios from "axios";
import Navigation from "@/components/Navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DataPage = () => {
  const [chiData, setChiData] = useState(null);
  const [psychTerms, setPsychTerms] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("chicago");
  const [expandedTerm, setExpandedTerm] = useState(null);
  const [expandedCondition, setExpandedCondition] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/data/chicago-homicides`),
      axios.get(`${API}/data/psych-terms`),
      axios.get(`${API}/data/health-conditions`),
    ]).then(([chi, psych, health]) => {
      setChiData(chi.data);
      setPsychTerms(psych.data);
      setHealthData(health.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const tabs = [
    { id: "chicago", label: "Chicago DV Data", icon: BarChart3 },
    { id: "psych", label: "Psych Terms", icon: Brain },
    { id: "health", label: "Health Conditions", icon: HeartPulse },
  ];

  const categoryColors = {
    "Mental Health": "bg-purple-50 text-purple-600 border-purple-200",
    "Chronic Pain": "bg-orange-50 text-orange-600 border-orange-200",
    "Gastrointestinal": "bg-amber-50 text-amber-600 border-amber-200",
    "Cardiovascular": "bg-red-50 text-red-600 border-red-200",
    "Immune System": "bg-blue-50 text-blue-600 border-blue-200",
    "Neurological": "bg-indigo-50 text-indigo-600 border-indigo-200",
    "Behavioral": "bg-teal-50 text-teal-600 border-teal-200",
    "Physical": "bg-pink-50 text-pink-600 border-pink-200",
    "Metabolic": "bg-emerald-50 text-emerald-600 border-emerald-200",
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navigation />
      <section className="pt-28 pb-20 px-6 lg:px-12" data-testid="data-section">
        <div className="container mx-auto max-w-5xl">
          <Link to="/" className="inline-flex items-center gap-2 text-[#4FB3D4] hover:text-[#4FB3D4]/80 mb-8 text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>

          <div className="text-center mb-10">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2A3A] mb-3" data-testid="data-title">Research & Data</h1>
            <p className="text-sm text-[#1A2A3A]/60 max-w-lg mx-auto">
              Knowledge is power. Understanding the data, the language, and the health impact of abuse helps us protect ourselves and advocate for change.
            </p>
          </div>

          {/* Disclaimer */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-8 flex items-start gap-2" data-testid="data-disclaimer">
            <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">This data is for educational purposes only. We are not medical or legal professionals. We do not diagnose, treat, or cure any condition. Sources cited where available.</p>
          </div>

          {/* Tabs */}
          <div className="grid grid-cols-3 gap-3 mb-8">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex flex-col items-center gap-2 px-4 py-5 rounded-2xl text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${activeTab === tab.id ? "bg-[#4FB3D4] text-white shadow-lg scale-[1.02]" : "bg-white text-[#1A2A3A]/60 border-2 border-[#E5E7EB] hover:border-[#4FB3D4] hover:shadow-md"}`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="h-6 w-6" />
                <span>{tab.label}</span>
                <span className={`text-[10px] font-normal ${activeTab === tab.id ? "text-white/70" : "text-[#1A2A3A]/40"}`}>
                  {tab.id === "chicago" ? "Tap to view data" : tab.id === "psych" ? "20 definitions" : "20 conditions"}
                </span>
              </button>
            ))}
          </div>

          {loading ? (
            <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="animate-pulse h-24 bg-white rounded-xl" />)}</div>
          ) : (
            <>
              {/* Chicago DV Data */}
              {activeTab === "chicago" && chiData && (
                <div className="space-y-6" data-testid="chicago-data">
                  <h2 className="font-heading text-2xl font-semibold text-[#1A2A3A]">{chiData.title}</h2>
                  <p className="text-xs text-[#1A2A3A]/50">{chiData.source_notes}</p>

                  {/* Illinois Statewide */}
                  <Card className="rounded-xl shadow-card border border-[#E5E7EB]/50" data-testid="illinois-data-card">
                    <CardContent className="p-6">
                      <h3 className="font-heading text-lg font-semibold text-[#1A2A3A] mb-4 flex items-center gap-2">
                        <BarChart3 className="h-5 w-5 text-red-500" /> Illinois Statewide DV Deaths
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead><tr className="border-b border-[#E5E7EB]">
                            <th className="text-left py-2 font-semibold text-[#1A2A3A]">Year</th>
                            <th className="text-left py-2 font-semibold text-[#1A2A3A]">Deaths</th>
                            <th className="text-left py-2 font-semibold text-[#1A2A3A]">Notes</th>
                          </tr></thead>
                          <tbody>
                            {chiData.illinois_statewide?.map((row, i) => (
                              <tr key={i} className="border-b border-[#E5E7EB]/50">
                                <td className="py-2.5 font-medium text-[#1A2A3A]">{row.year}</td>
                                <td className="py-2.5"><span className="text-red-600 font-bold">{row.deaths || row.dv_homicides || "—"}</span></td>
                                <td className="py-2.5 text-[#1A2A3A]/60 text-xs">{row.notes}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Chicago Specific */}
                  <Card className="rounded-xl shadow-card border border-[#E5E7EB]/50" data-testid="chicago-specific-card">
                    <CardContent className="p-6">
                      <h3 className="font-heading text-lg font-semibold text-[#1A2A3A] mb-4">Chicago-Specific Data</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead><tr className="border-b border-[#E5E7EB]">
                            <th className="text-left py-2 font-semibold text-[#1A2A3A]">Year</th>
                            <th className="text-left py-2 font-semibold text-[#1A2A3A]">DV Homicides</th>
                            <th className="text-left py-2 font-semibold text-[#1A2A3A]">Notes</th>
                          </tr></thead>
                          <tbody>
                            {chiData.chicago_specific?.map((row, i) => (
                              <tr key={i} className="border-b border-[#E5E7EB]/50">
                                <td className="py-2.5 font-medium text-[#1A2A3A]">{row.year}</td>
                                <td className="py-2.5"><span className="text-red-600 font-bold">{row.dv_homicides || "—"}</span></td>
                                <td className="py-2.5 text-[#1A2A3A]/60 text-xs">{row.notes}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Prior Complaints */}
                  <Card className="rounded-xl shadow-card border border-red-200 bg-red-50/30" data-testid="prior-complaints-card">
                    <CardContent className="p-6">
                      <h3 className="font-heading text-lg font-semibold text-[#1A2A3A] mb-3">Prior Complaints & System Involvement</h3>
                      <p className="text-sm text-[#1A2A3A]/60 mb-4">{chiData.prior_complaints_context?.description}</p>
                      <ul className="space-y-2">
                        {chiData.prior_complaints_context?.data_points?.map((point, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-[#1A2A3A]/70">
                            <div className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0 mt-2" /> {point}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>

                  {/* By Sex */}
                  <Card className="rounded-xl shadow-card border border-[#E5E7EB]/50" data-testid="by-sex-card">
                    <CardContent className="p-6">
                      <h3 className="font-heading text-lg font-semibold text-[#1A2A3A] mb-4">Domestic Battery by Sex</h3>
                      <div className="space-y-3">
                        {chiData.domestic_battery_by_sex?.data?.map((row, i) => (
                          <div key={i} className="flex items-start justify-between gap-4 py-2 border-b border-[#E5E7EB]/50 last:border-0">
                            <div>
                              <p className="text-sm font-medium text-[#1A2A3A]">{row.category}</p>
                              <p className="text-xs text-[#1A2A3A]/50">{row.notes}</p>
                            </div>
                            {row.percentage && <span className="text-sm font-bold text-[#1A2A3A] whitespace-nowrap">{row.percentage}</span>}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                    <p className="text-sm text-red-700 font-medium">{chiData.call_to_action}</p>
                  </div>
                </div>
              )}

              {/* Psych Terms */}
              {activeTab === "psych" && psychTerms && (
                <div className="space-y-4" data-testid="psych-terms">
                  <h2 className="font-heading text-2xl font-semibold text-[#1A2A3A] mb-2">{psychTerms.title}</h2>
                  {psychTerms.terms?.map((t, i) => (
                    <Card key={i} className="bg-white rounded-xl shadow-sm border border-[#E5E7EB]/50" data-testid={`psych-term-${i}`}>
                      <button className="w-full p-4 flex items-start justify-between gap-3 text-left" onClick={() => setExpandedTerm(expandedTerm === i ? null : i)}>
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-lg bg-[#A69BE0]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Brain className="h-4 w-4 text-[#A69BE0]" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-[#1A2A3A] text-sm">{i + 1}. {t.term}</h3>
                            {expandedTerm !== i && <p className="text-xs text-[#1A2A3A]/50 mt-0.5 line-clamp-1">{t.definition}</p>}
                          </div>
                        </div>
                        {expandedTerm === i ? <ChevronUp className="h-4 w-4 text-[#1A2A3A]/40 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-[#1A2A3A]/40 flex-shrink-0" />}
                      </button>
                      {expandedTerm === i && (
                        <div className="px-4 pb-4 ml-11">
                          <p className="text-sm text-[#1A2A3A]/70 leading-relaxed">{t.definition}</p>
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              )}

              {/* Health Conditions */}
              {activeTab === "health" && healthData && (
                <div className="space-y-4" data-testid="health-conditions">
                  <h2 className="font-heading text-2xl font-semibold text-[#1A2A3A] mb-2">{healthData.title}</h2>
                  <Card className="bg-gradient-to-br from-[#4FB3D4]/5 to-[#DCD6F7]/10 rounded-xl border-0 p-5 mb-4">
                    <p className="text-sm text-[#1A2A3A]/70 leading-relaxed"><strong className="text-[#1A2A3A]">The Science:</strong> {healthData.mechanism}</p>
                  </Card>
                  {healthData.conditions?.map((c, i) => (
                    <Card key={i} className={`rounded-xl shadow-sm border overflow-hidden ${categoryColors[c.category] || "bg-gray-50 text-gray-600 border-gray-200"}`} data-testid={`health-condition-${i}`}>
                      <button className="w-full p-4 flex items-start justify-between gap-3 text-left" onClick={() => setExpandedCondition(expandedCondition === i ? null : i)}>
                        <div className="flex items-start gap-3">
                          <HeartPulse className="h-4 w-4 flex-shrink-0 mt-1" />
                          <div>
                            <h3 className="font-semibold text-[#1A2A3A] text-sm">{i + 1}. {c.name}</h3>
                            <span className="text-[10px] font-medium opacity-70">{c.category}</span>
                          </div>
                        </div>
                        {expandedCondition === i ? <ChevronUp className="h-4 w-4 text-[#1A2A3A]/40 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-[#1A2A3A]/40 flex-shrink-0" />}
                      </button>
                      {expandedCondition === i && (
                        <div className="px-4 pb-4 ml-7">
                          <p className="text-sm text-[#1A2A3A]/70 leading-relaxed">{c.description}</p>
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <footer className="py-10 px-6 lg:px-12 bg-[#1A2A3A] text-white">
        <div className="container mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="font-heading text-lg font-bold">ThryvLife</div>
          <p className="text-white/40 text-xs">&copy; 2025 ThryvLife by Brian Bass (BriVonB). All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default DataPage;
